/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useRef } from "react";
import dayjs from "dayjs";
import { Col, Row } from "react-awesome-styled-grid";
import { When } from "react-if";
import { Button, theme } from "@nordcloud/gnui";
import { Role, useApplicationLazyQuery } from "~/generated/graphql";
import {
  BreadcrumbsBox,
  BrickLoader,
  Pagination,
  TableLoader,
  UniversalWrap,
  UnstyledLink,
} from "~/components";
import { dateFormat } from "~/constants";
import { useDisclosure, useQueryState, useScrollAfterPaging } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { ACTIONS, Can } from "~/services/auth";
import { useBillingPeriod, useCustomers } from "~/services/customers";
import { showSimpleInfo } from "~/services/toast";
import { isNotNil } from "~/tools";
import {
  ApplicationEditSidebar,
  ApplicationItem,
  CostAllocation,
  DefaultApplication,
} from "./components";
import { useMappedApplications } from "./mapping";

export function ApplicationsPage() {
  const { userRole } = useCustomers();
  const scrollToRef = useRef<HTMLDivElement>(null);

  const {
    state: { page, limit },
  } = useQueryState();

  const { isOpen, close, toggle } = useDisclosure();

  const { isCurrentBillingPeriod } = useBillingPeriod();

  const { applications, count, loading, error } = useMappedApplications({
    limit,
    page,
    year: dayjs().format(dateFormat.year),
  });

  const [getApplication, { data: appData, loading: isAppLoading }] =
    useApplicationLazyQuery({ fetchPolicy: "no-cache" });

  useScrollAfterPaging({ scrollToRef, enabled: !loading });

  return (
    <Row>
      <Col>
        <BreadcrumbsBox title="Applications">
          <Can action={ACTIONS.addApplication}>
            <UnstyledLink to={ROUTES.applications.create}>
              <Button icon="plus">Add new Application</Button>
            </UnstyledLink>
          </Can>
        </BreadcrumbsBox>
        <div ref={scrollToRef}>
          <UniversalWrap
            errorProps={{ error }}
            loaderProps={{ loading, Component: Loader }}
          >
            <When condition={userRole !== Role.TeamLead}>
              <CostAllocation />
            </When>
            <Can action={ACTIONS.viewDefaultApplication}>
              <DefaultApplication />
            </Can>
            {applications.map((application) => (
              <ApplicationItem
                key={application.id}
                application={application}
                getAppHandler={getApplication}
                editHandler={(value) =>
                  isCurrentBillingPeriod
                    ? toggle(value)
                    : showSimpleInfo(
                        "Editing application in the previous billing period is restricted"
                      )
                }
              />
            ))}
            <Pagination count={count} />
            <ApplicationEditSidebar
              isOpen={isOpen}
              appLoading={isAppLoading}
              defaultValues={{
                ...appData?.application,
                description: appData?.application?.description ?? "",
                owner: appData?.application?.owner ?? undefined,
                contacts: (appData?.application?.contacts ?? []).filter(
                  isNotNil
                ),
              }}
              onClose={close}
            />
          </UniversalWrap>
        </div>
      </Col>
    </Row>
  );
}

function Loader() {
  return (
    <>
      <BrickLoader
        viewBox="0 0 4 1"
        style={{
          display: "block",
          height: "auto",
          marginBottom: theme.spacing.spacing04,
        }}
      />
      <BrickLoader
        viewBox="0 0 14 1"
        style={{
          display: "block",
          height: "auto",
          marginBottom: theme.spacing.spacing04,
        }}
      />
      <TableLoader
        gapY={6}
        gapX={0}
        rows={[
          {
            count: 4,
            height: 100,
            items: [
              {
                widthPercent: 100,
              },
            ],
          },
        ]}
      />
    </>
  );
}
